import { DateTime, IANAZone, Settings } from 'luxon';
import { use, useCallback, useContext, useEffect, useRef, useMemo } from 'react';

import ConfigurationContext from '../Contexts/ConfigurationContext';
import { UserPreferencesContext } from '../Contexts/UserPreferencesContext';
import { isDate, isDateTime, isString } from './TypeUtils';

export const useDateUtils = () => {

    const { configurationData } = useContext(ConfigurationContext);
    const { userPreferences } = use(UserPreferencesContext);

    const use24hr = useRef(userPreferences?.use24hr ?? true);
    useMemo(() => {
        const nextValue = userPreferences?.use24hr ?? true;
        if (use24hr.current !== nextValue) {
            use24hr.current = nextValue;
        }
    }, [userPreferences?.use24hr]);

    const convertUTCToShortDateTime = useCallback((utcDateTime: string | Date | DateTime | null) => {
        let localTime = convertUTCToLocalTime(utcDateTime);
        if (localTime === null)
            return "";

        return localTime.toLocaleString(DateTime.DATE_SHORT) + " " + localTime.toLocaleString(use24hr ? DateTime.TIME_24_SIMPLE : DateTime.TIME_SIMPLE);
    }, []);

    const convertUTCToShortDateTimeSecs = (utcDateTime: string | Date | DateTime | null) => {
        let localTime = convertUTCToLocalTime(utcDateTime);
        if (localTime === null)
            return "";

        return localTime.toLocaleString(DateTime.DATE_SHORT) + " " + localTime.toLocaleString(use24hr ? DateTime.TIME_24_WITH_SECONDS : DateTime.TIME_WITH_SECONDS);
    };

    const convertUTCToShortDateOnly = (utcDateTime: string | Date | DateTime | null) => {
        let localTime = convertUTCToLocalTime(utcDateTime);
        if (localTime === null)
            return "";

        return localTime.toLocaleString(DateTime.DATE_SHORT);
    };

    const convertUTCToMedDateTimeSecs = (utcDateTime: string) => {
        let localTime = convertUTCToLocalTime(utcDateTime);
        if (localTime === null)
            return "";

        return localTime.toLocaleString(DateTime.DATE_MED) + " " + localTime.toLocaleString(use24hr ? DateTime.TIME_24_WITH_SECONDS : DateTime.TIME_WITH_SECONDS);
    };

    const getDatePickerFormat = (dateOnly: boolean) => {
        var timeFormat = use24hr ? "HH:mm" : "hh:mm:aa";
        var dateFormat = dateOnly ? "P" : "P " + timeFormat;

        return { dateFormat, timeFormat };
    };

    const setDefaultTimeZoneOfSite = useCallback(() => {
        let locale = configurationData?.find((element: any) => element.name === "Locale");
        let timeZone = configurationData?.find((element: any) => element.name === "TimeZoneName");

        if (locale?.value && Settings.defaultLocale !== locale.value) {
            Settings.defaultLocale = locale.value;
        }

        if (timeZone?.value && Settings.defaultZone !== timeZone.value) {
            if (!IANAZone.isValidZone(timeZone.value)) {
                console.error(`Invalid IANA timezone provided: ${timeZone.value}, defaulting to UTC`);
                Settings.defaultZone = "Etc/UTC";
                return;
            }
            Settings.defaultZone = timeZone.value;
        }

    }, [configurationData]);

    useEffect(() => {
        setDefaultTimeZoneOfSite();
    }, [setDefaultTimeZoneOfSite]);

    return { convertUTCToShortDateTime, convertUTCToShortDateTimeSecs, convertUTCToShortDateOnly, convertUTCToMedDateTimeSecs, getDatePickerFormat, setDefaultTimeZoneOfSite };
};

/**
 * Converts a Date or Luxon DateTime object to a date-time string in JSON format (YYYY-MM-DDTHH:mm:ss)
 * Z is omitted in case a local time is sent to the back end
 * Millisecond accuracy is not required
 * Used for setting the value of DateTime pickers
 */
export const convertDateToJsonFormat = (date: any) => {
    let dateString = "";
    switch (true) {
        case isDate(date):
            dateString = `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}T${`${date.getHours()}`.padStart(2, "0")}:${`${date.getMinutes()}`.padStart(2, "0")}:${`${date.getSeconds()}`.padStart(2, "0")}`;
            break;
        case isDateTime(date):
            dateString = `${date.year}-${date.month.toString().padStart(2, "0")}-${date.day.toString().padStart(2, "0")}T${date.hour.toString().padStart(2, "0")}:${date.minute.toString().padStart(2, "0")}:${date.second.toString().padStart(2, "0")}`;
            break;
        default:
            dateString = "";
    }

    return dateString;
};

/**
 * Converts a JS Date or ISO string to Luxon DateTime
 */
export const convertToDateTime = (date: any) => {
    switch (true) {
        case date === null || date === undefined:
            return null;
        case isString(date):
            if (date === "") {
                return null;
            }
            return DateTime.fromISO(date, { zone: Settings.defaultZone, locale: Settings.defaultLocale });
        case isDate(date):
            return DateTime.fromISO(convertDateToJsonFormat(date)).setZone(Settings.defaultZone).setLocale(Settings.defaultLocale);
        default:
            throw new Error(`Unable to convert ${date} to a Date Time`);
    }
};

/**
 * Converts local time to UTC
 * Used for setting UTC values in DTOs from DateTime pickers using local time
 */
export const convertLocalTimeZoneToUTC = (localDateTime: any): DateTime => {
    let date = null;
    switch (true) {
        case isString(localDateTime):
            date = DateTime.fromISO(localDateTime, { zone: Settings.defaultZone }).setLocale(Settings.defaultLocale).toUTC();
            break;
        case isDate(localDateTime):
            date = DateTime.fromJSDate(localDateTime, { zone: Settings.defaultZone }).setLocale(Settings.defaultLocale).toUTC();
            break;
        case isDateTime(localDateTime):
            date = localDateTime.toUTC();
            break;
        default:
            throw new Error(`Unable to convert ${localDateTime} to UTC time`);
    }

    if (!date) {
        console.error(`Failed to convert local date ${localDateTime} to a UTC date, returning null`);
    }

    return date;
};

export const convertUTCToLocalTime = (utcDateTime: any) => {
    let date = null;

    switch (true) {
        case isString(utcDateTime):
            date = DateTime.fromISO(utcDateTime, { zone: "Etc/UTC" }).setLocale(Settings.defaultLocale).setZone();
            break;
        case isDate(utcDateTime):
            date = DateTime.fromJSDate(utcDateTime, { zone: "Etc/UTC" }).setLocale(Settings.defaultLocale).setZone();
            break;
        case isDateTime(utcDateTime):
            date = utcDateTime.setLocale(Settings.defaultLocale).setZone();
            break;
        case utcDateTime === undefined || utcDateTime === null:
            date = null;
            break;
        default:
            throw new Error(`Unable to convert ${utcDateTime} to Local time`);
    }

    if (date !== null && !date.isValid) {
        throw new Error(`Unable to convert ${utcDateTime} to a valid date time`);
    }

    return date;
};